import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { CheckoutDialog } from '@/components/marketplace/CheckoutDialog';
import {
  Store, Package, ShoppingCart, Code2, RefreshCw, Loader2,
  ChevronLeft, AlertCircle, ShoppingBag, Search,
} from 'lucide-react';
import { useBrowseStalls } from '@/hooks/useBrowseStalls';
import { useBrowseProducts } from '@/hooks/useBrowseProducts';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { formatPrice, type Product, type Stall } from '@/lib/marketplace';
import type { NostrEvent } from '@nostrify/nostrify';
import { Input } from '@/components/ui/input';

// ── Types ────────────────────────────────────────────────────────────────────

interface CartItem { product: Product; quantity: number }

interface BrowseMarketplaceProps {
  onViewJson: (event: NostrEvent) => void;
}

// ── Merchant avatar chip ──────────────────────────────────────────────────────

function MerchantChip({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const meta = author.data?.metadata;
  const name = meta?.name ?? genUserName(pubkey);
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <Avatar className="h-4 w-4 shrink-0">
        <AvatarImage src={meta?.picture} />
        <AvatarFallback className="text-[8px]">{name[0].toUpperCase()}</AvatarFallback>
      </Avatar>
      <span className="text-[10px] text-muted-foreground truncate">{name}</span>
    </div>
  );
}

// ── Stall grid card ───────────────────────────────────────────────────────────

function StallGridCard({
  event, stall, merchantPubkey, onClick,
}: {
  event: NostrEvent;
  stall: Stall;
  merchantPubkey: string;
  onClick: () => void;
}) {
  return (
    <Card
      className="cursor-pointer transition-all hover:shadow-md hover:border-primary/40 group"
      onClick={onClick}
    >
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-sm font-semibold truncate group-hover:text-primary transition-colors">
              {stall.name}
            </CardTitle>
            <CardDescription className="text-xs mt-0.5 line-clamp-2 h-8">
              {stall.description || 'No description provided'}
            </CardDescription>
          </div>
          <Badge variant="outline" className="text-[10px] shrink-0 font-mono">
            {stall.currency}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-2">
        <MerchantChip pubkey={merchantPubkey} />
        <div className="flex flex-wrap gap-1">
          {stall.shipping.slice(0, 3).map(z => (
            <Badge key={z.id} variant="secondary" className="text-[10px] px-1.5">
              {z.name || 'Zone'} · {formatPrice(z.cost, stall.currency)}
            </Badge>
          ))}
          {stall.shipping.length > 3 && (
            <Badge variant="secondary" className="text-[10px] px-1.5">
              +{stall.shipping.length - 3} more
            </Badge>
          )}
        </div>
        <Button size="sm" variant="outline" className="w-full h-7 gap-1.5 text-xs mt-1">
          <ShoppingBag className="h-3 w-3" />
          Browse Products
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Browse product card ───────────────────────────────────────────────────────

function BrowseProductCard({
  event, product, stall, cartQty, onAddToCart, onViewJson,
}: {
  event: NostrEvent;
  product: Product;
  stall: Stall;
  cartQty: number;
  onAddToCart: () => void;
  onViewJson: () => void;
}) {
  const outOfStock = product.quantity !== null && product.quantity <= 0;

  return (
    <Card className="flex flex-col transition-all hover:shadow-md overflow-hidden">
      {product.images?.[0] && (
        <div className="aspect-video w-full overflow-hidden bg-muted shrink-0">
          <img
            src={product.images[0]}
            alt={product.name}
            className="w-full h-full object-cover transition-transform hover:scale-105"
            onError={e => {
              (e.currentTarget as HTMLImageElement).parentElement!.style.display = 'none';
            }}
          />
        </div>
      )}
      <CardContent className="flex flex-col flex-1 p-4 gap-3">
        <div className="flex-1 min-h-0">
          <p className="text-sm font-semibold leading-tight line-clamp-2">{product.name}</p>
          {product.description && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-3">{product.description}</p>
          )}
        </div>

        {/* Specs */}
        {Array.isArray(product.specs) && product.specs.length > 0 && (
          <div className="space-y-0.5">
            {(product.specs as unknown as string[][]).slice(0, 3).map(([k, v], i) => (
              <div key={i} className="flex gap-1 text-[10px] text-muted-foreground">
                <span className="font-medium shrink-0">{k}:</span>
                <span className="truncate">{v}</span>
              </div>
            ))}
          </div>
        )}

        {/* Categories */}
        {product.categories && product.categories.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {product.categories.map(cat => (
              <Badge key={cat} variant="secondary" className="text-[9px] px-1.5 py-0">
                #{cat}
              </Badge>
            ))}
          </div>
        )}

        {/* Price + stock row */}
        <div className="flex items-center justify-between gap-2 mt-auto pt-1">
          <span className="text-base font-bold text-primary">
            {formatPrice(product.price, stall.currency)}
          </span>
          <Badge
            variant={outOfStock ? 'destructive' : product.quantity === null ? 'secondary' : 'outline'}
            className="text-[10px] shrink-0"
          >
            {outOfStock ? 'Out of stock' : product.quantity === null ? '∞ Unlimited' : `${product.quantity} left`}
          </Badge>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            size="sm"
            className="h-8 gap-1.5 text-xs flex-1 relative"
            onClick={onAddToCart}
            disabled={outOfStock}
          >
            <ShoppingCart className="h-3.5 w-3.5" />
            {cartQty > 0 ? `In Cart (${cartQty})` : 'Add to Cart'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 shrink-0 text-muted-foreground"
            onClick={onViewJson}
            title="View raw event JSON"
          >
            <Code2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Products loading skeleton ─────────────────────────────────────────────────

function ProductsSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1, 2, 3, 4, 5, 6].map(i => (
        <Card key={i}>
          <Skeleton className="aspect-video w-full rounded-t-lg rounded-b-none" />
          <CardContent className="p-4 space-y-3">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-4/5" />
            <div className="flex items-center justify-between pt-1">
              <Skeleton className="h-6 w-20" />
              <Skeleton className="h-5 w-16" />
            </div>
            <Skeleton className="h-8 w-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── Stalls loading skeleton ───────────────────────────────────────────────────

function StallsSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1, 2, 3, 4, 5, 6].map(i => (
        <Card key={i}>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div className="space-y-1.5 flex-1">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-3/4" />
              </div>
              <Skeleton className="h-5 w-12 ml-2" />
            </div>
            <Skeleton className="h-3 w-24" />
            <div className="flex gap-1">
              <Skeleton className="h-5 w-20" />
              <Skeleton className="h-5 w-16" />
            </div>
            <Skeleton className="h-7 w-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── Main browse component ─────────────────────────────────────────────────────

export function BrowseMarketplace({ onViewJson }: BrowseMarketplaceProps) {
  const {
    data: stalls = [],
    isLoading: stallsLoading,
    isError: stallsError,
    refetch: refetchStalls,
    isFetching: stallsFetching,
  } = useBrowseStalls();

  // Currently selected stall
  const [activeStallId, setActiveStallId] = useState<string | null>(null);
  const [activeMerchant, setActiveMerchant] = useState<string | null>(null);

  const activeStallEntry = stalls.find(
    s => s.stall.id === activeStallId && s.merchantPubkey === activeMerchant
  ) ?? null;

  const {
    data: products = [],
    isLoading: productsLoading,
    isError: productsError,
    isFetching: productsFetching,
  } = useBrowseProducts(activeMerchant, activeStallId);

  // Search / filter stalls
  const [stallSearch, setStallSearch] = useState('');
  const filteredStalls = stallSearch.trim()
    ? stalls.filter(s =>
        s.stall.name.toLowerCase().includes(stallSearch.toLowerCase()) ||
        s.stall.description?.toLowerCase().includes(stallSearch.toLowerCase())
      )
    : stalls;

  // Cart (per-merchant to keep things clean)
  const [cart, setCart] = useState<CartItem[]>([]);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const addToCart = (product: Product) =>
    setCart(c => {
      const existing = c.find(ci => ci.product.id === product.id);
      if (existing) {
        return c.map(ci =>
          ci.product.id === product.id ? { ...ci, quantity: ci.quantity + 1 } : ci
        );
      }
      return [...c, { product, quantity: 1 }];
    });

  const clearCart = () => setCart([]);
  const cartTotal = cart.reduce((s, i) => s + i.quantity, 0);
  const cartQty = (productId: string) =>
    cart.find(ci => ci.product.id === productId)?.quantity ?? 0;

  const openStall = (stallId: string, merchantPubkey: string) => {
    // Clear cart when switching stalls to avoid cross-merchant orders
    if (activeStallId !== stallId || activeMerchant !== merchantPubkey) {
      clearCart();
    }
    setActiveStallId(stallId);
    setActiveMerchant(merchantPubkey);
  };

  const backToStalls = () => {
    setActiveStallId(null);
    setActiveMerchant(null);
    clearCart();
  };

  // ── Stall list view ──────────────────────────────────────────────────────
  if (!activeStallEntry) {
    return (
      <div className="space-y-4">
        {/* Header row */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <p className="text-sm text-muted-foreground">
              {stallsLoading
                ? 'Discovering stalls on the Nostr network…'
                : `${stalls.length} stall${stalls.length !== 1 ? 's' : ''} found across the network`}
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="gap-2 h-8 shrink-0"
            onClick={() => refetchStalls()}
            disabled={stallsFetching}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${stallsFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Search */}
        {stalls.length > 3 && (
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search stalls…"
              value={stallSearch}
              onChange={e => setStallSearch(e.target.value)}
              className="pl-8 h-9 text-sm"
            />
          </div>
        )}

        {/* Loading */}
        {stallsLoading && <StallsSkeleton />}

        {/* Error */}
        {stallsError && !stallsLoading && (
          <Card className="border-destructive/30">
            <CardContent className="py-8 text-center space-y-2">
              <AlertCircle className="h-8 w-8 text-destructive mx-auto" />
              <p className="text-sm font-medium">Failed to load stalls</p>
              <p className="text-xs text-muted-foreground">Check your relay connections and try again</p>
              <Button size="sm" variant="outline" onClick={() => refetchStalls()}>
                Retry
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Empty */}
        {!stallsLoading && !stallsError && stalls.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center space-y-3">
              <Store className="h-10 w-10 text-muted-foreground mx-auto" />
              <p className="text-sm font-medium">No stalls found</p>
              <p className="text-xs text-muted-foreground max-w-xs mx-auto">
                No NIP-15 stalls were found on your connected relays. Try refreshing or
                adding more relays in the Relays page.
              </p>
              <Button size="sm" variant="outline" onClick={() => refetchStalls()}>
                <RefreshCw className="h-3.5 w-3.5 mr-2" />
                Retry
              </Button>
            </CardContent>
          </Card>
        )}

        {/* No search results */}
        {!stallsLoading && stalls.length > 0 && filteredStalls.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="py-8 text-center space-y-2">
              <p className="text-sm text-muted-foreground">No stalls match "{stallSearch}"</p>
              <Button size="sm" variant="ghost" onClick={() => setStallSearch('')}>
                Clear search
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Stall grid */}
        {!stallsLoading && filteredStalls.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredStalls.map(({ event, stall, merchantPubkey }) => (
              <StallGridCard
                key={`${merchantPubkey}:${stall.id}`}
                event={event}
                stall={stall}
                merchantPubkey={merchantPubkey}
                onClick={() => openStall(stall.id, merchantPubkey)}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Product list view (stall selected) ───────────────────────────────────
  const { stall, merchantPubkey } = activeStallEntry;

  return (
    <div className="space-y-4">
      {/* Back bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 h-8 -ml-1"
          onClick={backToStalls}
        >
          <ChevronLeft className="h-4 w-4" />
          All Stalls
        </Button>

        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Store className="h-4 w-4 text-primary shrink-0" />
          <span className="text-sm font-semibold truncate">{stall.name}</span>
          <Badge variant="outline" className="text-[10px] font-mono shrink-0">{stall.currency}</Badge>
        </div>

        {productsFetching && (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
        )}
      </div>

      {/* Stall description */}
      {stall.description && (
        <p className="text-sm text-muted-foreground">{stall.description}</p>
      )}

      {/* Merchant info */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Sold by</span>
        <MerchantChip pubkey={merchantPubkey} />
      </div>

      {/* Cart bar */}
      {cartTotal > 0 && (
        <div className="flex items-center gap-3 p-3 bg-primary/5 rounded-lg border border-primary/20">
          <ShoppingCart className="h-4 w-4 text-primary shrink-0" />
          <span className="text-sm font-medium">
            {cartTotal} item{cartTotal !== 1 ? 's' : ''} in cart
          </span>
          <div className="ml-auto flex gap-2">
            <Button
              size="sm"
              className="h-7 gap-1.5 text-xs"
              onClick={() => setCheckoutOpen(true)}
            >
              Checkout
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-muted-foreground"
              onClick={clearCart}
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Products loading */}
      {productsLoading && <ProductsSkeleton />}

      {/* Products error */}
      {productsError && !productsLoading && (
        <Card className="border-destructive/30">
          <CardContent className="py-8 text-center space-y-2">
            <AlertCircle className="h-8 w-8 text-destructive mx-auto" />
            <p className="text-sm font-medium">Failed to load products</p>
            <p className="text-xs text-muted-foreground">
              Could not fetch products from this merchant's relay
            </p>
          </CardContent>
        </Card>
      )}

      {/* No products */}
      {!productsLoading && !productsError && products.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center space-y-2">
            <Package className="h-10 w-10 text-muted-foreground mx-auto" />
            <p className="text-sm font-medium">No products in this stall</p>
            <p className="text-xs text-muted-foreground">
              This merchant hasn't listed any products yet
            </p>
          </CardContent>
        </Card>
      )}

      {/* Product grid */}
      {!productsLoading && products.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map(({ event, product }) => (
            <BrowseProductCard
              key={`${event.pubkey}:${product.id}`}
              event={event}
              product={product}
              stall={stall}
              cartQty={cartQty(product.id)}
              onAddToCart={() => addToCart(product)}
              onViewJson={() => onViewJson(event)}
            />
          ))}
        </div>
      )}

      {/* Checkout dialog */}
      {checkoutOpen && cart.length > 0 && (
        <CheckoutDialog
          open={checkoutOpen}
          onOpenChange={v => {
            setCheckoutOpen(v);
            if (!v) clearCart();
          }}
          merchantPubkey={merchantPubkey}
          stall={stall}
          cart={cart}
        />
      )}
    </div>
  );
}
